import React, { useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Search, Sparkles, MapPin, Compass, ArrowRight, ArrowLeft,
  Tent, Dog, Snowflake, Star, Copy, Check, Instagram, Facebook, MessageCircle,
} from "lucide-react";
import { SOCIAL } from "../lib/social";
import { withUtm } from "../lib/utm";

/**
 * /share/simplified-search — 4-scene 9:16 reel pushing the new one-tap
 * search & compare flow on the landing page. Bright orange palette so it
 * pops against any background when shared on Reels / Stories / TikTok.
 *
 * Scenes:
 *   01 — HOOK: "Struggling to find a campsite?"
 *   02 — THE NEW FLOW: 3-step idiot-proof process visual
 *   03 — WHAT YOU GET: side-by-side comparison + map result preview
 *   04 — THE LINK: QR + buttons back to the home page widget
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");

const HOME_URL = withUtm(PUBLIC_URL + "/#search", {
  source: "simplified-search-reel",
  medium: "reel",
  campaign: "1tap-search-2026",
});

const CAPTION = `Struggling to find a campsite that actually fits what you need? 🏕️

Most apps make you wade through filters. We just rebuilt it.

Open CampCompass UK → tick what matters (dogs · year-round · near you · price · rating · pitch type) → hit Search → done.

You get a like-for-like comparison of every UK campsite that matches, sorted by distance from you. 22,179 hand-checked sites. Real prices. Real reviews. No affiliate spin.

→ Try it: camp-compass.uk
✓ Free to browse · £1.99/mo Pro · 14-day free trial

#camping #ukcamping #campsite #campervan #caravan #motorhome #wildcamping #campingtrip`;

export default function SimplifiedSearchReel() {
  const [copied, setCopied] = useState(false);
  const copy = (s) => { try { navigator.clipboard.writeText(s); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {} };

  return (
    <div className="min-h-screen bg-[#0e0e0e] text-white pb-20" data-testid="simplified-search-reel">
      <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <Link to="/" className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-wider text-white/70 hover:text-white">
          <ArrowLeft className="w-3 h-3" /> Back to app
        </Link>
        <div className="mono text-[10px] uppercase tracking-wider text-[#ff4500]">/share/simplified-search</div>
      </header>

      {/* 4 vertical scenes (9:16 each), stacked so the page works both as a
          live reel preview AND a shareable URL. */}
      <div className="max-w-md mx-auto px-4 sm:px-6 space-y-8 mt-6">
        {/* SCENE 01 — HOOK */}
        <Scene n="01" label="HOOK · 4S" tint="from-[#0e0e0e] via-[#1a1a1a] to-[#ff4500]">
          <div className="text-[#fde047] mono text-xs uppercase tracking-[0.22em] mb-4">CAMPCOMPASS UK</div>
          <h2 className="font-display text-5xl sm:text-6xl font-bold leading-[0.95] tracking-tight mb-6">
            Struggling to find a <span className="text-[#fde047]">campsite</span> that fits?
          </h2>
          <p className="text-white/85 text-sm mono uppercase tracking-wider">Most apps bury the filters.</p>
          <p className="text-[#ff4500] text-sm mono uppercase tracking-wider mt-1 font-bold">We just rebuilt it.</p>
        </Scene>

        {/* SCENE 02 — THE NEW FLOW */}
        <Scene n="02" label="THE FLOW · 5S" tint="from-[#ff4500] via-[#e63e00] to-[#0e0e0e]">
          <div className="text-white mono text-xs uppercase tracking-[0.22em] mb-4">NEW · 1-TAP SEARCH</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6">
            Three taps.<br/>That's it.
          </h2>
          <div className="space-y-3">
            <FlowStep n={1} icon={Sparkles} title="Pick what matters" sub="Dogs · year-round · price · rating · near you" />
            <FlowStep n={2} icon={Search} title="Hit Search" sub="One button. No fiddling." />
            <FlowStep n={3} icon={Compass} title="Compare" sub="Side-by-side · distance-sorted · real prices" />
          </div>
        </Scene>

        {/* SCENE 03 — WHAT YOU GET */}
        <Scene n="03" label="WHAT YOU GET · 4S" tint="from-[#fde047] via-[#fbbf24] to-[#ff4500]">
          <div className="text-[#0e0e0e] mono text-xs uppercase tracking-[0.22em] mb-4 font-bold">YOU GET</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6 text-[#0e0e0e]">
            Every UK campsite<br/>that matches —<br/>side by side.
          </h2>
          <ul className="space-y-2 text-[#0e0e0e]">
            <Bullet icon={Star}><strong>Real ratings</strong> from real campers</Bullet>
            <Bullet icon={MapPin}><strong>22,179 hand-checked sites</strong></Bullet>
            <Bullet icon={Tent}><strong>Pitch type</strong> · facilities · pet rules</Bullet>
            <Bullet icon={Sparkles}>No affiliate spin · No hidden listings</Bullet>
          </ul>
        </Scene>

        {/* SCENE 04 — CTA */}
        <Scene n="04" label="CTA · 3S" tint="from-[#0e0e0e] via-[#0e0e0e] to-[#ff4500]">
          <div className="text-[#fde047] mono text-xs uppercase tracking-[0.22em] mb-4">FREE TO TRY</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6">
            Try it now.<br/>camp-compass.uk
          </h2>
          <div className="bg-white text-[#0e0e0e] p-4 rounded-2xl flex items-center gap-4 mb-6">
            <QRCodeSVG value={HOME_URL} size={88} bgColor="#ffffff" fgColor="#0e0e0e" />
            <div className="min-w-0">
              <div className="mono text-[10px] uppercase tracking-wider text-[#888]">Scan to open</div>
              <div className="font-display text-lg font-bold leading-tight">camp-compass.uk</div>
              <div className="mono text-[10px] uppercase tracking-wider text-[#ff4500] mt-1">14-day free trial</div>
            </div>
          </div>
          <Link to="/" className="block w-full text-center bg-[#ff4500] hover:bg-[#e63e00] text-white py-3 rounded-xl font-bold uppercase tracking-wider text-sm mb-3" data-testid="reel-cta-home">
            Open the app →
          </Link>
        </Scene>

        {/* CAPTION + SHARE BUTTONS */}
        <div className="bg-white text-[#0e0e0e] rounded-2xl p-4 sm:p-5">
          <div className="mono text-[10px] uppercase tracking-wider text-[#888] mb-2">Pre-written caption · tap to copy</div>
          <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed mb-3">{CAPTION}</pre>
          <button
            onClick={() => copy(CAPTION)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#ff4500] hover:bg-[#e63e00] text-white text-xs font-bold uppercase tracking-wider transition-colors"
            data-testid="reel-copy-caption"
          >
            {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy caption</>}
          </button>
        </div>

        {/* Quick-share row */}
        <div className="flex flex-wrap gap-2">
          <a href={SOCIAL?.instagram?.url || "https://instagram.com/campcompassuk"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs mono uppercase tracking-wider"><Instagram className="w-3 h-3"/> Instagram</a>
          <a href={SOCIAL?.facebook?.url || "https://facebook.com/campcompassuk"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs mono uppercase tracking-wider"><Facebook className="w-3 h-3"/> Facebook</a>
          <a href={`https://wa.me/?text=${encodeURIComponent(HOME_URL)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs mono uppercase tracking-wider"><MessageCircle className="w-3 h-3"/> WhatsApp</a>
        </div>
      </div>
    </div>
  );
}

function Scene({ n, label, tint, children }) {
  return (
    <section
      className={`relative w-full aspect-[9/16] rounded-3xl overflow-hidden p-7 sm:p-9 flex flex-col justify-end bg-gradient-to-br ${tint} shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] border border-white/10`}
      data-testid={`scene-${n}`}
    >
      <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
        <span className="mono text-[10px] uppercase tracking-[0.18em] text-white/80 bg-black/30 backdrop-blur px-2 py-1 rounded">SCENE {n}</span>
        <span className="mono text-[10px] uppercase tracking-[0.18em] text-white/70">{label}</span>
      </div>
      <div className="relative z-10">{children}</div>
    </section>
  );
}

function FlowStep({ n, icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3 bg-white/10 backdrop-blur rounded-xl p-3 border border-white/20">
      <div className="w-8 h-8 rounded-full bg-[#fde047] text-[#0e0e0e] font-bold flex items-center justify-center shrink-0 font-display">{n}</div>
      <div className="min-w-0">
        <div className="font-display font-bold leading-tight flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {title}</div>
        <div className="text-[12px] text-white/80 mono uppercase tracking-wider mt-0.5">{sub}</div>
      </div>
    </div>
  );
}

function Bullet({ icon: Icon, children }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 mt-0.5 shrink-0 text-[#ff4500]" strokeWidth={2.4} />
      <span>{children}</span>
    </li>
  );
}
